<script setup lang="ts">
import type {
  PlayerPartyInvite,
  PlayerSocialFriendRequest,
} from '@/api/public'
import PlayerAvatar from '@/components/PlayerAvatar.vue'
import {
  playerIncomingPartyInviteLimit,
  playerIncomingPartyInviteLimitNotice,
  playerPartyInviteBulkDeclineConfirmation,
} from '@/lib/playerSocialPresentation'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'

const props = defineProps<{
  requests: PlayerSocialFriendRequest[]
  partyInvites: PlayerPartyInvite[]
  partyInviteCount: number
  loading: boolean
  actionPending: boolean
  error: string | null
  disabled: boolean
  closeRequested: boolean
}>()

const emit = defineEmits<{
  accept: [requestID: string]
  decline: [requestID: string]
  ignorePlayer: [steamID: string]
  openPartyInvite: [inviteID: string]
  declineAllPartyInvites: []
  opened: []
  clearError: []
}>()

const root = ref<HTMLElement | null>(null)
const trigger = ref<HTMLButtonElement | null>(null)
const panel = ref<HTMLElement | null>(null)
const open = ref(false)
const pendingFocus = ref<{
  notificationKey: string
  index: number
} | null>(null)
const bulkPartyInviteFocusKey = '\u0000party-invites-bulk'
const incomingPartyInviteCount = computed(() => {
  const rawCount = Number.isFinite(props.partyInviteCount)
    ? Math.trunc(props.partyInviteCount)
    : 0
  return Math.max(0, rawCount, props.partyInvites.length)
})
const notificationCount = computed(() => (
  props.requests.length + incomingPartyInviteCount.value
))
const partyInviteLimitNotice = computed(() => (
  playerIncomingPartyInviteLimitNotice(incomingPartyInviteCount.value)
))

watch(() => props.disabled, disabled => {
  if (disabled) close(false)
})
watch(() => props.closeRequested, requested => {
  if (requested) close(false)
})
watch(
  [
    () => props.requests.map(request => request.id).join('\u0000'),
    () => props.partyInvites.map(invite => `party-${invite.id}`).join('\u0000'),
    () => props.actionPending,
  ],
  async () => {
    const pending = pendingFocus.value
    if (!pending) return
    const notificationKeys = [
      ...props.requests.map(request => request.id),
      ...props.partyInvites.map(invite => `party-${invite.id}`),
    ]
    if (notificationKeys.includes(pending.notificationKey)) {
      if (!props.actionPending) pendingFocus.value = null
      return
    }
    await nextTick()
    if (!open.value) {
      pendingFocus.value = null
      return
    }
    if (props.actionPending) {
      panel.value?.focus({ preventScroll: true })
      return
    }
    pendingFocus.value = null
    const cards = panel.value?.querySelectorAll<HTMLElement>('.player-notification-card')
    const nextCard = cards && cards.length > 0
      ? cards.item(Math.min(pending.index, cards.length - 1))
      : null
    const nextAction = nextCard?.querySelector<HTMLButtonElement>('button:not(:disabled)')
    const focusTarget = nextAction ?? panel.value
    focusTarget?.focus({ preventScroll: true })
  },
)

onMounted(() => {
  window.addEventListener('keydown', handleWindowKeydown)
  window.addEventListener('pointerdown', handleWindowPointerDown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleWindowKeydown)
  window.removeEventListener('pointerdown', handleWindowPointerDown)
})

function requestName(request: PlayerSocialFriendRequest): string {
  return request.player.persona_name || request.player.steam_id
}

function inviterName(invite: PlayerPartyInvite): string {
  return invite.invited_by_persona_name || invite.invited_by_steam_id
}

function toggle(): void {
  if (props.disabled) return
  if (open.value) {
    close()
    return
  }
  open.value = true
  emit('opened')
  void nextTick(() => panel.value?.focus({ preventScroll: true }))
}

function close(restoreFocus = true): void {
  if (!open.value) return
  open.value = false
  if (restoreFocus) {
    void nextTick(() => trigger.value?.focus({ preventScroll: true }))
  }
}

function handleWindowKeydown(event: KeyboardEvent): void {
  if (!open.value || event.key !== 'Escape') return
  event.preventDefault()
  close()
}

function handleWindowPointerDown(event: PointerEvent): void {
  if (!open.value) return
  const target = event.target
  if (target instanceof Node && root.value?.contains(target)) return
  close(false)
}

function actOnRequest(
  action: 'accept' | 'decline',
  requestID: string,
  index: number,
): void {
  if (props.actionPending) return
  pendingFocus.value = { notificationKey: requestID, index }
  if (action === 'accept') {
    emit('accept', requestID)
  } else {
    emit('decline', requestID)
  }
}

function ignorePlayer(
  steamID: string,
  notificationKey: string,
  index: number,
): void {
  if (props.actionPending) return
  pendingFocus.value = { notificationKey, index }
  emit('ignorePlayer', steamID)
}

function openPartyInvite(inviteID: string): void {
  emit('openPartyInvite', inviteID)
  close(false)
}

function declineAllPartyInvites(): void {
  if (props.actionPending || incomingPartyInviteCount.value <= 1) return
  if (!window.confirm(
    playerPartyInviteBulkDeclineConfirmation(),
  )) {
    return
  }
  pendingFocus.value = {
    notificationKey: bulkPartyInviteFocusKey,
    index: props.requests.length,
  }
  emit('declineAllPartyInvites')
}
</script>

<template>
  <div ref="root" class="player-notification-center">
    <button
      ref="trigger"
      class="player-notification-trigger"
      type="button"
      :class="{ active: notificationCount > 0 }"
      :disabled="disabled"
      :aria-label="notificationCount ? `Уведомления: ${notificationCount}` : 'Уведомления'"
      aria-haspopup="dialog"
      :aria-expanded="open"
      aria-controls="player-notification-panel"
      @click="toggle"
    >
      <img
        src="/shared/assets/notification_icon_off_psd.png"
        alt=""
        aria-hidden="true"
      />
      <span v-if="notificationCount" class="player-notification-count" aria-hidden="true">
        {{ notificationCount > 9 ? '9+' : notificationCount }}
      </span>
    </button>
    <span class="player-notification-status" role="status" aria-live="polite">
      {{ notificationCount ? `Новых уведомлений: ${notificationCount}` : '' }}
    </span>

    <Transition name="player-notification-panel">
      <section
        v-if="open"
        id="player-notification-panel"
        ref="panel"
        class="player-notification-panel"
        role="dialog"
        aria-labelledby="player-notification-title"
        tabindex="-1"
      >
        <header>
          <h2 id="player-notification-title">Уведомления</h2>
          <button type="button" aria-label="Закрыть уведомления" @click="close()">
            <img src="/shared/assets/x_close_png.png" alt="" aria-hidden="true" />
          </button>
        </header>

        <div v-if="error" class="player-notification-error" role="alert">
          <span>{{ error }}</span>
          <button type="button" aria-label="Скрыть ошибку" @click="$emit('clearError')">×</button>
        </div>

        <div
          v-if="loading && notificationCount === 0"
          class="player-notification-empty"
          role="status"
        >
          Загружаем уведомления…
        </div>
        <div v-else-if="notificationCount === 0" class="player-notification-empty">
          Новых уведомлений нет.
        </div>
        <div v-else class="player-notification-list">
          <article
            v-for="(request, index) in requests"
            :key="request.id"
            class="player-notification-card"
          >
            <PlayerAvatar
              :name="requestName(request)"
              :avatar-url="request.player.avatar_url"
              size="sm"
              decorative
            />
            <div>
              <strong>{{ requestName(request) }}</strong>
              <span>Хочет добавить вас в друзья</span>
            </div>
            <div class="player-notification-actions">
              <button
                class="accept"
                type="button"
                :disabled="actionPending"
                :aria-label="`Принять заявку в друзья от ${requestName(request)}`"
                @click="actOnRequest('accept', request.id, index)"
              >
                Принять
              </button>
              <button
                class="decline"
                type="button"
                :disabled="actionPending"
                :aria-label="`Отклонить заявку в друзья от ${requestName(request)}`"
                @click="actOnRequest('decline', request.id, index)"
              >
                Отклонить
              </button>
              <button
                class="ignore"
                type="button"
                :disabled="actionPending"
                :aria-label="`Игнорировать ${requestName(request)}`"
                @click="ignorePlayer(request.player.steam_id, request.id, index)"
              >
                <img src="/shared/assets/x_close_png.png" alt="" aria-hidden="true" />
                <span>Игнорировать</span>
              </button>
            </div>
          </article>
          <section
            v-if="incomingPartyInviteCount > 0"
            class="player-notification-party-summary"
            aria-labelledby="player-notification-party-title"
          >
            <div class="player-notification-party-heading">
              <div>
                <h3 id="player-notification-party-title">Приглашения в группу</h3>
                <span>
                  {{ incomingPartyInviteCount }} / {{ playerIncomingPartyInviteLimit }}
                </span>
              </div>
              <button
                v-if="incomingPartyInviteCount > 1"
                type="button"
                class="player-notification-decline-all"
                data-party-invite-action="decline-all"
                :disabled="actionPending"
                @click="declineAllPartyInvites"
              >
                Отклонить все
              </button>
            </div>
            <p
              v-if="partyInviteLimitNotice"
              class="player-notification-party-limit"
              role="status"
            >
              {{ partyInviteLimitNotice }}
            </p>
          </section>
          <article
            v-for="(invite, inviteIndex) in partyInvites"
            :key="`party-${invite.id}`"
            class="player-notification-card party"
          >
            <PlayerAvatar
              :name="inviterName(invite)"
              :avatar-url="invite.invited_by_avatar_url"
              size="sm"
              decorative
            />
            <div>
              <strong>{{ inviterName(invite) }}</strong>
              <span>Приглашает в группу · {{ invite.party.members.length }} / 5</span>
            </div>
            <div class="player-notification-actions">
              <button
                class="open-party"
                type="button"
                :disabled="actionPending"
                :aria-label="`Открыть приглашение в группу от ${inviterName(invite)}`"
                @click="openPartyInvite(invite.id)"
              >
                Открыть приглашение
              </button>
              <button
                class="ignore"
                type="button"
                :disabled="actionPending"
                :aria-label="`Игнорировать ${inviterName(invite)}`"
                @click="ignorePlayer(
                  invite.invited_by_steam_id,
                  `party-${invite.id}`,
                  requests.length + inviteIndex,
                )"
              >
                <img src="/shared/assets/x_close_png.png" alt="" aria-hidden="true" />
                <span>Игнорировать</span>
              </button>
            </div>
          </article>
        </div>
      </section>
    </Transition>
  </div>
</template>
